import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import useFetch from 'use-http';
import { Loader2 } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';

export default function ServiceQueueUserInformationDialog({
  open,
  onOpenChange,
  onAccept,
  initialPhoneNumber = '',
  initialEmail = '',
  initialName = '',
  initialSurname = '',
  initialPersonalCode = '',
  serviceId,
  workplaceId,
}) {
  const [step, setStep] = React.useState('confirmation');
  const [name, setName] = React.useState(initialName);
  const [surname, setSurname] = React.useState(initialSurname);
  const [personalCode, setPersonalCode] = React.useState(initialPersonalCode);
  const [phoneNumber, setPhoneNumber] = React.useState(initialPhoneNumber);
  const [email, setEmail] = React.useState(initialEmail);
  const [errors, setErrors] = React.useState({
    name: '',
    surname: '',
    personalCode: '',
    phoneNumber: '',
    email: '',
    submit: '',
  });
  const [loading, setLoading] = React.useState(false);

  const { post: registerToQueue } = useFetch(`/Registrations/RegisterToQueue`);

  React.useEffect(() => {
    if (open) {
      setStep('confirmation');
      setName(initialName || '');
      setSurname(initialSurname || '');
      setPersonalCode(initialPersonalCode || '');
      setPhoneNumber(initialPhoneNumber || '');
      setEmail(initialEmail || '');
      setErrors({
        name: '',
        surname: '',
        personalCode: '',
        phoneNumber: '',
        email: '',
        submit: '',
      });
    }
  }, [
    open,
    initialName,
    initialSurname,
    initialPersonalCode,
    initialPhoneNumber,
    initialEmail,
  ]);

  const validate = () => {
    const nextErrors = {
      name: '',
      surname: '',
      personalCode: '',
      phoneNumber: '',
      email: '',
      submit: '',
    };

    const trimmedName = name.trim();
    const trimmedSurname = surname.trim();
    const trimmedPersonalCode = personalCode.trim();
    const trimmedPhone = phoneNumber.trim();
    const trimmedEmail = email.trim();

    if (!trimmedName) {
      nextErrors.name = 'Vardas yra privalomas.';
    }

    if (!trimmedSurname) {
      nextErrors.surname = 'Pavardė yra privaloma.';
    }

    if (!trimmedPersonalCode) {
      nextErrors.personalCode = 'Asmens kodas yra privalomas.';
    } else if (!/^\d{11}$/.test(trimmedPersonalCode)) {
      nextErrors.personalCode = 'Asmens kodą turi sudaryti 11 skaitmenų.';
    }

    if (!trimmedPhone) {
      nextErrors.phoneNumber = 'Telefono numeris yra privalomas.';
    }

    if (!trimmedEmail) {
      nextErrors.email = 'El. paštas yra privalomas.';
    } else if (!/^\S+@\S+\.\S+$/.test(trimmedEmail)) {
      nextErrors.email = 'Neteisingas el. pašto formatas.';
    }

    setErrors(nextErrors);

    return (
      !nextErrors.name &&
      !nextErrors.surname &&
      !nextErrors.personalCode &&
      !nextErrors.phoneNumber &&
      !nextErrors.email
    );
  };

  const handleConfirm = async () => {
    if (!validate()) {
      return;
    }

    setLoading(true);

    const request = {
      name: name.trim(),
      surname: surname.trim(),
      personalCode: personalCode.trim(),
      email: email.trim(),
      phoneNumber: phoneNumber.trim(),
      serviceId,
      workplaceId,
    };

    const response = await registerToQueue(request);

    setLoading(false);

    if (!response) {
      toast.error('Nepavyko išsiųsti užklausos');
      setErrors((prev) => ({
        ...prev,
        submit: 'Nepavyko užregistruoti į eilę.',
      }));
      return;
    }

    toast.success('Dėkojame, artimiausiu metu su Jumis susisieksime.');
    onOpenChange(false);
    onAccept(response);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen);
      }}
    >
      <Toaster />
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Registracija į laukiančiųjų eilę</DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-6 max-h-[75dvh] overflow-y-auto">
          {step === 'confirmation' ? (
            <p className="text-center leading-6">
              Informuojame, kad laukiančiųjų eilė tai nėra patvirtinta
              registracija vizitui. Numatoma laukimo trukmė gali būti iki 1
              mėnesio ir ilgiau . Atsiradus laikui, susisieksime su Jumis
              nurodytais kontaktais. Jeigu sutinkate, spauskite "Tęsti".
            </p>
          ) : (
            <div className="space-y-5">
              {step !== 'confirmation' && (
                <p className="text-sm ">
                  Registruodamiesi patvirtinate, kad Jūsų nurodytais
                  kontaktiniais duomenimis gali susisiekti Affidea klientų
                  aptarnavimo centro komanda.
                </p>
              )}

              <div className="space-y-2">
                <Label htmlFor="service-queue-name">Vardas</Label>
                <Input
                  id="service-queue-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Vardas"
                />
                {errors.name ? (
                  <p className="text-sm text-destructive">{errors.name}</p>
                ) : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="service-queue-surname">Pavardė</Label>
                <Input
                  id="service-queue-surname"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  placeholder="Pavardė"
                />
                {errors.surname ? (
                  <p className="text-sm text-destructive">{errors.surname}</p>
                ) : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="service-queue-personal-code">
                  Asmens kodas
                </Label>
                <Input
                  id="service-queue-personal-code"
                  inputMode="numeric"
                  value={personalCode}
                  onChange={(e) => setPersonalCode(e.target.value)}
                  placeholder="Asmens kodas"
                />
                {errors.personalCode ? (
                  <p className="text-sm text-destructive">
                    {errors.personalCode}
                  </p>
                ) : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="service-queue-phone">Telefono numeris</Label>
                <Input
                  id="service-queue-phone"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="+37061234567"
                />
                {errors.phoneNumber ? (
                  <p className="text-sm text-destructive">
                    {errors.phoneNumber}
                  </p>
                ) : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="service-queue-email">El. paštas</Label>
                <Input
                  id="service-queue-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vardas@pastas.lt"
                />
                {errors.email ? (
                  <p className="text-sm text-destructive">{errors.email}</p>
                ) : null}
              </div>

              {errors.submit ? (
                <p className="text-sm text-destructive">{errors.submit}</p>
              ) : null}
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          {step === 'confirmation' ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Atšaukti
              </Button>
              <Button variant="secondary" onClick={() => setStep('form')}>
                Tęsti
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Atšaukti
              </Button>
              <Button
                variant="secondary"
                onClick={handleConfirm}
                disabled={loading}
              >
                {loading && (
                  <Loader2
                    className="h-4 w-4 animate-spin"
                    aria-hidden="true"
                  />
                )}
                Registruotis
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
